// ═══════════════════════════════════════════════════════════════
// Intake Portal — Mobile-first field intake view
// Route: /intakeportal
// Auth: existing GAB login, self-contained (no AppShell)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useIntakeStore } from '../../store/intakeStore';
import type { Individual } from '../../types/intake';
import {
  IconUserPlus, IconSearch, IconUsers, IconLogout,
  IconCheck, IconChevronRight, IconArrowLeft,
} from '@tabler/icons-react';
import '../shelter/portal/ShelterPortal.css';

// ── Types ──────────────────────────────────────────────────────

type Tab = 'register' | 'lookup' | 'roster';

const TABS: { id: Tab; label: string; Icon: typeof IconUserPlus }[] = [
  { id: 'register', label: 'Register', Icon: IconUserPlus },
  { id: 'lookup',   label: 'Lookup',   Icon: IconSearch },
  { id: 'roster',   label: 'Roster',   Icon: IconUsers },
];

const TRIAGE_OPTIONS = [
  { value: 'green',  label: 'Green',  sublabel: 'Minor / None',  color: '#037730' },
  { value: 'yellow', label: 'Yellow', sublabel: 'Delayed care',  color: '#C68700' },
  { value: 'red',    label: 'Red',    sublabel: 'Immediate',     color: '#D33423' },
  { value: 'black',  label: 'Black',  sublabel: 'Expectant',     color: '#1a1a2e' },
];

function triageColor(t: string) {
  const map: Record<string, string> = { green: '#037730', yellow: '#C68700', red: '#D33423', black: '#1a1a2e' };
  return map[t] || '#64748b';
}

function TriagePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
      {TRIAGE_OPTIONS.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            padding: '10px 10px 8px',
            borderRadius: 6,
            border: `2px solid ${value === opt.value ? opt.color : '#e2e8f0'}`,
            background: value === opt.value ? opt.color + '18' : '#fff',
            color: value === opt.value ? opt.color : '#64748b',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            textAlign: 'left', lineHeight: 1.3, fontFamily: 'inherit',
          }}
        >
          <span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: opt.color, marginBottom: 5 }} />
          {opt.label}
          <span style={{ display: 'block', fontWeight: 400, fontSize: 10, opacity: 0.8, marginTop: 1 }}>{opt.sublabel}</span>
        </button>
      ))}
    </div>
  );
}

// ── Login ──────────────────────────────────────────────────────

function PortalLogin() {
  const { login } = useAuthStore();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required.'); return; }
    setLoading(true); setError('');
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || 'Login failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="sp-login-wrap">
      <div className="sp-login-card">
        <div className="sp-login-logo-row">
          <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-login-og-logo" />
          <div className="sp-login-divider" />
          <span className="sp-login-product-name">Intake Portal</span>
        </div>
        <div className="sp-login-title">Field Intake</div>
        <div className="sp-login-sub">Sign in to register and manage individuals</div>
        <form onSubmit={submit}>
          {error && <div className="sp-error">{error}</div>}
          <div className="sp-field">
            <label className="sp-label">Email</label>
            <input className="sp-input" type="email" placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)} autoComplete="email" inputMode="email" />
          </div>
          <div className="sp-field">
            <label className="sp-label">Password</label>
            <input className="sp-input" type="password" placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          <button className="sp-btn sp-btn-primary" type="submit" disabled={loading} style={{ marginTop: 8 }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Individual Detail Overlay ──────────────────────────────────

function IndividualDetail({ ind, onClose, onUpdated }: {
  ind: Individual;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { update, discharge } = useIntakeStore();
  const [triage, setTriage]               = useState(ind.triage || 'green');
  const [notes, setNotes]                 = useState(ind.notes || '');
  const [saving, setSaving]               = useState(false);
  const [discharging, setDischarging]     = useState(false);
  const [confirmDischarge, setConfirm]    = useState(false);
  const [saved, setSaved]                 = useState(false);
  const isDischarged = ind.status === 'discharged';
  const changed = triage !== (ind.triage || 'green') || notes !== (ind.notes || '');

  const handleSave = async () => {
    setSaving(true);
    try {
      await update(ind.id, { triage, notes });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated();
    } finally { setSaving(false); }
  };

  const handleDischarge = async () => {
    setDischarging(true);
    try {
      await discharge(ind.id);
      onUpdated();
      onClose();
    } finally { setDischarging(false); }
  };

  return (
    <div className="sp-drawer-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sp-drawer" style={{ paddingBottom: 40 }}>
        <div className="sp-drawer-handle" />

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#64748b', flexShrink: 0 }}>
            <IconArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#1e293b' }}>{ind.firstName} {ind.lastName}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Tag: {ind.tagCode || '—'}</div>
          </div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
            padding: '4px 9px', borderRadius: 20, flexShrink: 0,
            background: isDischarged ? '#f1f5f9' : triageColor(triage) + '18',
            color: isDischarged ? '#64748b' : triageColor(triage),
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: isDischarged ? '#94a3b8' : triageColor(triage) }} />
            {isDischarged ? 'Discharged' : triage.charAt(0).toUpperCase() + triage.slice(1)}
          </span>
        </div>

        {/* Info grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 20 }}>
          {ind.phone && <InfoRow label="Phone" value={ind.phone} />}
          {(ind.dateOfBirth || ind.dob) && <InfoRow label="Date of Birth" value={ind.dateOfBirth || ind.dob || ''} />}
          {ind.registeredBy && <InfoRow label="Registered By" value={ind.registeredBy} />}
          {ind.registeredAt && <InfoRow label="Registered" value={new Date(ind.registeredAt).toLocaleString()} />}
          {ind.emergencyContactName && (
            <InfoRow
              label="Emergency Contact"
              value={ind.emergencyContactName + (ind.emergencyContactPhone ? ` · ${ind.emergencyContactPhone}` : '')}
              wide
            />
          )}
          {ind.medicalNeeds && <InfoRow label="Medical Needs" value={ind.medicalNeeds} wide />}
        </div>

        {isDischarged ? (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Discharged</div>
            {ind.dischargedAt && <div style={{ fontSize: 12 }}>{new Date(ind.dischargedAt).toLocaleString()}</div>}
          </div>
        ) : (
          <>
            {/* Triage update */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
              Update Triage
            </div>
            <TriagePicker value={triage} onChange={setTriage} />

            {/* Notes */}
            <div className="sp-field" style={{ marginTop: 14 }}>
              <label className="sp-label">Notes</label>
              <textarea
                className="sp-input"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Medical needs, special circumstances…"
                style={{ resize: 'none', fontFamily: 'inherit' }}
              />
            </div>

            <button
              className="sp-btn sp-btn-primary"
              onClick={handleSave}
              disabled={saving || !changed}
              style={{ marginBottom: 10 }}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
            </button>

            {!confirmDischarge ? (
              <button className="sp-btn sp-btn-danger" onClick={() => setConfirm(true)}>
                Discharge Individual
              </button>
            ) : (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#B12525', marginBottom: 10 }}>
                  Confirm discharge {ind.firstName} {ind.lastName}?
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleDischarge}
                    disabled={discharging}
                    className="sp-btn sp-btn-primary"
                    style={{ flex: 1, padding: '10px', fontSize: 13, background: '#B12525' }}
                  >
                    {discharging ? 'Discharging…' : 'Yes, Discharge'}
                  </button>
                  <button
                    onClick={() => setConfirm(false)}
                    className="sp-btn sp-btn-secondary"
                    style={{ flex: 1, padding: '10px', fontSize: 13 }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1e293b' }}>{value}</div>
    </div>
  );
}

// ── Individual Card ────────────────────────────────────────────

function IndCard({ ind, onClick }: { ind: Individual; onClick: () => void }) {
  const isDischarged = ind.status === 'discharged';
  return (
    <div onClick={onClick} className="sp-ungrouped-card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 12, height: 12, borderRadius: '50%', background: isDischarged ? '#94a3b8' : triageColor(ind.triage), flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="sp-survivor-name">{ind.firstName} {ind.lastName}</div>
        <div className="sp-survivor-meta">
          Tag: {ind.tagCode || '—'}{ind.phone ? ` · ${ind.phone}` : ''}{isDischarged ? ' · Discharged' : ''}
        </div>
      </div>
      <IconChevronRight size={14} color="#94a3b8" />
    </div>
  );
}

// ── Register Tab ───────────────────────────────────────────────

function RegisterTab() {
  const { user, activeIncident } = useAuthStore();
  const { create } = useIntakeStore();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [phone, setPhone]         = useState('');
  const [dob, setDob]             = useState('');
  const [triage, setTriage]       = useState('green');
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState<{ name: string; tag: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    setSaving(true);
    try {
      const tagCode = Math.random().toString(36).slice(2, 8).toUpperCase();
      await create({
        firstName: firstName.trim(), lastName: lastName.trim(),
        phone: phone.trim(), dateOfBirth: dob, dob,
        triage, notes: notes.trim(), tagCode,
        intakeMethod: 'field_portal',
        registeredBy: user?.email || '',
        registeredAt: new Date().toISOString(),
        incidentId: activeIncident?.id ?? null,
        status: 'active',
      });
      setDone({ name: `${firstName.trim()} ${lastName.trim()}`, tag: tagCode });
      setFirstName(''); setLastName(''); setPhone(''); setDob(''); setNotes(''); setTriage('green');
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 56, paddingBottom: 80 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#037730', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <IconCheck size={32} color="#fff" />
        </div>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#1e293b', marginBottom: 4 }}>Registered!</div>
        <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>{done.name}</div>
        <div style={{ display: 'inline-block', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 28px', marginBottom: 32 }}>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>Tag Code</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', letterSpacing: 3 }}>{done.tag}</div>
        </div>
        <div style={{ padding: '0 16px' }}>
          <button className="sp-btn sp-btn-primary" onClick={() => setDone(null)}>Register Another</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ padding: '16px 16px 80px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
        Individual Information
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">First Name <span style={{ color: '#ef4444' }}>*</span></label>
          <input className="sp-input" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First" required />
        </div>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Last Name <span style={{ color: '#ef4444' }}>*</span></label>
          <input className="sp-input" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last" required />
        </div>
      </div>

      <div className="sp-field">
        <label className="sp-label">Phone</label>
        <input className="sp-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 000-0000" />
      </div>

      <div className="sp-field">
        <label className="sp-label">Date of Birth</label>
        <input className="sp-input" type="date" value={dob} onChange={e => setDob(e.target.value)} />
      </div>

      <div className="sp-field">
        <label className="sp-label">Triage Status</label>
        <TriagePicker value={triage} onChange={setTriage} />
      </div>

      <div className="sp-field">
        <label className="sp-label">Notes</label>
        <textarea
          className="sp-input"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Medical needs, special circumstances…"
          style={{ resize: 'none', fontFamily: 'inherit' }}
        />
      </div>

      <button
        className="sp-btn sp-btn-primary"
        type="submit"
        disabled={saving || !firstName.trim() || !lastName.trim()}
      >
        {saving ? 'Registering…' : 'Register Individual'}
      </button>
    </form>
  );
}

// ── Lookup Tab ─────────────────────────────────────────────────

function LookupTab({ onSelect }: { onSelect: (ind: Individual) => void }) {
  const { individuals, fetch } = useIntakeStore();
  const [q, setQ] = useState('');

  useEffect(() => { fetch(); }, []);

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const lower = q.toLowerCase();
    return individuals.filter(i =>
      `${i.firstName} ${i.lastName}`.toLowerCase().includes(lower) ||
      (i.tagCode || '').toLowerCase().includes(lower) ||
      (i.phone || '').includes(q)
    ).slice(0, 30);
  }, [q, individuals]);

  return (
    <div style={{ padding: '16px 16px 80px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
        Search by name, tag, or phone
      </div>
      <input
        className="sp-input"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search…"
        style={{ marginBottom: 14 }}
        autoFocus
      />
      {q.trim() && results.length === 0 && (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '32px 0' }}>No matches found</div>
      )}
      {results.map(ind => <IndCard key={ind.id} ind={ind} onClick={() => onSelect(ind)} />)}
    </div>
  );
}

// ── Roster Tab ─────────────────────────────────────────────────

function RosterTab({ onSelect }: { onSelect: (ind: Individual) => void }) {
  const { individuals, fetch, loading } = useIntakeStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => { fetch(); }, []);

  const active = useMemo(() =>
    individuals.filter(i =>
      i.status !== 'discharged' &&
      (!activeIncident || String(i.incidentId) === String(activeIncident.id))
    ),
    [individuals, activeIncident]
  );

  const counts = useMemo(() => ({
    red:    active.filter(i => i.triage === 'red').length,
    yellow: active.filter(i => i.triage === 'yellow').length,
    green:  active.filter(i => i.triage === 'green').length,
    black:  active.filter(i => i.triage === 'black').length,
  }), [active]);

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Loading…</div>;

  return (
    <div style={{ padding: '16px 16px 80px' }}>
      {/* Triage summary */}
      {active.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'Red',    count: counts.red,    color: '#D33423' },
            { label: 'Yellow', count: counts.yellow, color: '#C68700' },
            { label: 'Green',  count: counts.green,  color: '#037730' },
            { label: 'Black',  count: counts.black,  color: '#1a1a2e' },
          ].map(t => (
            <div key={t.label} className="sp-stat" style={{ padding: '8px 4px' }}>
              <div className="sp-stat-val" style={{ fontSize: 20, color: t.color }}>{t.count}</div>
              <div className="sp-stat-lbl">{t.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
        {active.length} active individual{active.length !== 1 ? 's' : ''}
      </div>

      {active.length === 0 && (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '32px 0' }}>No active individuals</div>
      )}

      <div style={{ maxHeight: 'calc(100dvh - 280px)', overflowY: 'auto' }}>
        {active.map(ind => <IndCard key={ind.id} ind={ind} onClick={() => onSelect(ind)} />)}
      </div>
    </div>
  );
}

// ── Shell ──────────────────────────────────────────────────────

export default function IntakePortal() {
  const { isAuthenticated, logout } = useAuthStore();
  const { fetch } = useIntakeStore();
  const [tab, setTab]         = useState<Tab>('register');
  const [selected, setSelected] = useState<Individual | null>(null);

  if (!isAuthenticated) return <PortalLogin />;

  return (
    <div className="sp-shell">
      {/* Top bar */}
      <div className="sp-topbar">
        <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" className="sp-topbar-logo" />
        <div className="sp-topbar-divider" />
        <span className="sp-topbar-product">Intake Portal</span>
        <div style={{ flex: 1 }} />
        <button className="sp-topbar-icon-btn" onClick={logout} title="Sign out">
          <IconLogout size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="sp-content" style={{ paddingBottom: 0 }}>
        {tab === 'register' && <RegisterTab />}
        {tab === 'lookup'   && <LookupTab onSelect={setSelected} />}
        {tab === 'roster'   && <RosterTab onSelect={setSelected} />}
      </div>

      {/* Bottom nav */}
      <nav className="sp-bottom-nav">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={`sp-nav-btn${tab === id ? ' active' : ''}`}
            onClick={() => setTab(id)}
          >
            <Icon size={20} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* Individual detail overlay */}
      {selected && (
        <IndividualDetail
          ind={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => fetch()}
        />
      )}
    </div>
  );
}
